import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, MapPin, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setForm({ name: "", email: "", message: "" });
      toast.success("Message sent! We'll get back to you soon.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-white border-t-2 border-[#0F0F0F]/10">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A4A4A]">Get in Touch</span>
          <h2 className="mt-3 font-heading font-black tracking-tight text-3xl md:text-4xl text-[#0F0F0F] leading-tight">
            Have a question?<br /> We're here to help.
          </h2>
          <p className="mt-4 text-base md:text-lg text-[#4A4A4A] leading-relaxed max-w-md">
            Whether you're applying for yourself or referring someone you care about, reach out any time. Real people, ready to listen.
          </p>

          <div className="mt-8 space-y-4">
            {[
              { icon: Mail, label: "Email Us", value: "makeroomforbelievers@gmail.com" },
              { icon: MapPin, label: "Where We Serve", value: "Servicing Metro Atlanta and surrounding areas" },
            ].map((c) => (
              <div key={c.label} className="flex items-center gap-4">
                <span className="w-12 h-12 bg-[#FFC000] border-2 border-[#0F0F0F] flex items-center justify-center flex-shrink-0">
                  <c.icon size={20} className="text-[#0F0F0F]" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#4A4A4A]">{c.label}</p>
                  <p className="font-heading font-bold text-[#0F0F0F]">{c.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="bg-[#F9F9F8] border-2 border-[#0F0F0F] p-6 md:p-8 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wide">Name *</Label>
            <Input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Your name" className="mt-2" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Email *</Label>
            <Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="mt-2" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Message *</Label>
            <Textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="How can we help?" className="mt-2 min-h-[130px]" />
          </div>
          <button type="submit" data-testid="contact-submit-btn" disabled={loading} className="btn-brutal w-full disabled:opacity-60">
            {loading ? <><Loader2 size={18} className="mr-2 animate-spin" /> Sending...</> : "Send Message"}
          </button>
        </form>
      </div>
    </section>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#0F0F0F] text-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 grid md:grid-cols-3 gap-8">
        <div>
          <div className="bg-white inline-block p-3 border-2 border-white">
            <img src="/make-room-logo.png" alt="Make Room Independent Living" className="h-16 w-auto" />
          </div>
          <p className="mt-4 text-sm text-white/60 max-w-xs">Independent Living Program — safe housing, real support, lasting community.</p>
        </div>
        <div>
          <h4 className="font-heading font-bold text-sm uppercase tracking-wide text-[#FFC000]">Quick Links</h4>
          <ul className="mt-4 space-y-2 text-sm text-white/70">
            <li><a href="#about" className="hover:text-[#FFC000]">About</a></li>
            <li><a href="#programs" className="hover:text-[#FFC000]">Programs</a></li>
            <li><a href="#housing" className="hover:text-[#FFC000]">Housing</a></li>
            <li><a href="#apply" className="hover:text-[#FFC000]">How to Apply</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-heading font-bold text-sm uppercase tracking-wide text-[#FFC000]">Contact</h4>
          <ul className="mt-4 space-y-2 text-sm text-white/70">
            <li>makeroomforbelievers@gmail.com</li>
            <li>Servicing Metro Atlanta and surrounding areas</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/50">
        © {new Date().getFullYear()} Make Room for Believers Independent Living Program. All rights reserved.
      </div>
    </footer>
  );
};
